import { Period, PERIOD_NOUN } from "@/shared/domain/metrics";

/** `n` followed by `noun`, pluralised with a bare "s" (every period noun and
 *  "session" pluralise that way). */
function count(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? "" : "s"}`;
}

/**
 * The line under a chart card's title: how much data the plot is drawn from.
 *
 * `sessions` is exact rather than approximate — the metrics table is unique on
 * (metric, session), so the aggregate's per-bucket `total` counts distinct
 * sessions.
 *
 * `periods` is the SPAN the data covers, first data point to last, counted in
 * whole period buckets — not the number of buckets that carried data, and not
 * the selected range (which the range picker already states). Data in weeks 1
 * and 5 spans 5 weeks, however empty the three between them are.
 *
 * Null when nothing contributed: the chart draws its own empty state, and a
 * "0 sessions" line would only repeat it.
 */
export function chartSubtext(
  sessions: number,
  periods: number,
  period: Period,
): string | null {
  if (sessions === 0) {
    return null;
  }
  return `${count(sessions, "session")} over ${count(periods, PERIOD_NOUN[period])}`;
}

/**
 * The line under a RAW chart card's title: how many sessions the plot is drawn
 * from, and whether that is all of them.
 *
 * No period span, because a raw plot has no buckets — every point is one
 * session at its own time, and the range picker already states the window.
 *
 * `hasMore` is the honest half. A raw card loads one page and stops, so the
 * plot is usually a subset; the query asks for newest-first, so that subset is
 * the MOST RECENT sessions and the caption may say so. Once "Load all" has
 * drained the rest there is no subset left and the hedge goes away — which is
 * also how the user can tell the drain finished.
 *
 * `undated` counts sessions that matched the window but arrived with no
 * `max_timestamp_ns` and therefore no x coordinate. They are dropped rather
 * than plotted at a substitute time, and named here so an incomplete plot
 * never passes for a complete one.
 *
 * Null when there is nothing to report at all: the chart draws its own empty
 * state.
 */
export function rawChartSubtext(
  sessions: number,
  hasMore: boolean,
  undated = 0,
): string | null {
  if (sessions === 0 && undated === 0) {
    return null;
  }
  // Grouped: raw counts run to five figures, where "22000" is a smudge.
  const plotted = `${sessions.toLocaleString("en-US")} session${sessions === 1 ? "" : "s"}`;
  const head = hasMore ? `most recent ${plotted}` : plotted;
  return undated === 0
    ? head
    : `${head} (${undated.toLocaleString("en-US")} without a session time)`;
}
